import AppCard from '@nxsys/components/AppCard';
import { useGetPayrollSettingByEmployeeId } from '@nxsys/hooks/api/employee/useEmployeeApi';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Icons } from '@nxsys/types/icons';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Skeleton } from 'antd';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

const CurrentEmploymentYTD = () => {
  const { payrollCountry, user } = useSelector(
    (state: AppState) => state.appStore,
  );
  const employeeId = (user?.subcontractorId as any)?.id;
  const isIreland = payrollCountry === IRELAND;

  const {
    run: getPayrollSettingByEmployeeId,
    value: payrollSettingValue,
    loading,
  } = useGetPayrollSettingByEmployeeId();

  useEffect(() => {
    if (employeeId) {
      getPayrollSettingByEmployeeId(employeeId);
    }
  }, [employeeId]);

  const ytdValues = payrollSettingValue?.data?.ytdValues;

  const ukData = useMemo(
    () => [
      {
        label: 'Gross Pay',
        value: formatMoney(
          ytdValues?.grossIncome ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: <Icons.IconMoneyBill />,
      },
      {
        label: 'Net Pay',
        value: formatMoney(
          ytdValues?.netPay ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: <Icons.IconMoneyBillCheck />,
      },
      {
        label: 'Tax Paid',
        value: formatMoney(
          ytdValues?.tax ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: <Icons.IconMoneyBillMinus />,
      },
      {
        label: 'NI Employee',
        value: formatMoney(
          ytdValues?.eeNI ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: <Icons.IconCreditCardMinus />,
      },
      {
        label: 'NI Employer',
        value: formatMoney(
          ytdValues?.erNI ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: <Icons.IconCreditCardMinus />,
      },
      {
        label: 'Gross For Tax',
        value: formatMoney(
          ytdValues?.grossForTax ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: <Icons.IconMoneyBillMinus />,
      },
      {
        label: 'Gross For NI',
        value: formatMoney(
          ytdValues?.grossForNI ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: <Icons.IconMoneyBillMinus />,
      },
      {
        label: 'Apprenticeship Levy',
        value: formatMoney(
          ytdValues?.appLevy ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: <Icons.IconMoneyBillMinus />,
      },
      {
        label: 'Student Loan',
        value: formatMoney(
          ytdValues?.stLoan ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: <Icons.IconMoneyBillMinus />,
      },
      {
        label: 'Employee Pension',
        value: formatMoney(
          ytdValues?.eePension ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: <Icons.IconCreditCardMinus />,
      },
      {
        label: 'Employer Pension',
        value: formatMoney(
          ytdValues?.erPension ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: <Icons.IconCreditCardMinus />,
      },
      {
        label: 'Post Graduate Student Loan',
        value: formatMoney(
          ytdValues?.pgLoan ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: <Icons.IconMoneyBillMinus />,
      },
    ],
    [ytdValues, payrollCountry],
  );

  const irelandData = useMemo(
    () => [
      {
        label: 'Taxable Gross',
        value: formatMoney(
          ytdValues?.grossForTax ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: <Icons.IconMoneyBill />,
      },
      {
        label: 'Employee PRSIable Gross',
        value: formatMoney(
          ytdValues?.eePrsiGross ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: <Icons.IconMoneyBill />,
      },
      {
        label: 'Employer PRSIable Gross',
        value: formatMoney(
          ytdValues?.erPrsiGross ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: <Icons.IconMoneyBill />,
      },
      {
        label: 'USCable Gross',
        value: formatMoney(
          ytdValues?.uscGross ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: <Icons.IconMoneyBill />,
      },
      {
        label: 'Pensionable Gross',
        value: formatMoney(
          ytdValues?.pensionGross ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: <Icons.IconMoneyBill />,
      },
      {
        label: 'Tax',
        value: formatMoney(
          ytdValues?.tax ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: <Icons.IconMoneyBillMinus />,
      },
      {
        label: 'Employee PRSI',
        value: formatMoney(
          ytdValues?.eePRSI ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: <Icons.IconCreditCardMinus />,
      },
      {
        label: 'Employer PRSI',
        value: formatMoney(
          ytdValues?.erPRSI ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: <Icons.IconCreditCardMinus />,
      },
      {
        label: 'USC',
        value: formatMoney(
          ytdValues?.usc ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: <Icons.IconMoneyBillMinus />,
      },
      // Same as the payslip: MFF (NAERSA) and private pension are separate
      // lines, each shown only when there is a contribution (NX-6033)
      ...[
        {
          label: 'Employee MFF',
          amount: ytdValues?.eePension ?? 0,
        },
        {
          label: 'Employer MFF',
          amount: ytdValues?.erPension ?? 0,
        },
        {
          label: 'Employee Pension',
          amount: ytdValues?.eePrivatePension ?? 0,
        },
        {
          label: 'Employer Pension',
          amount: ytdValues?.erPrivatePension ?? 0,
        },
      ]
        .filter(({ amount }) => amount > 0)
        .map(({ label, amount }) => ({
          label,
          value: formatMoney(amount, showCurrencyByCountry(payrollCountry)),
          icon: <Icons.IconCreditCardMinus />,
        })),
      {
        label: 'LPT',
        value: formatMoney(
          ytdValues?.lptDeduction ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: <Icons.IconMoneyBillMinus />,
      },
    ],
    [ytdValues, payrollCountry],
  );

  const data = isIreland ? irelandData : ukData;

  if (loading && !payrollSettingValue) {
    return (
      <AppCard className="h-full">
        <div className="p-[20px]">
          <Skeleton active />
        </div>
      </AppCard>
    );
  }

  return (
    <AppCard className="h-full">
      <div className="p-[20px]">
        <div className="text-[16px] font-[500] mb-[6px]">
          Current Employment Year-To-Date
        </div>
        <div className="text-[12px] font-[400] text-neutral-500 mb-[16px]">
          A quick summary of your payment for the current employment year.
        </div>
        <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-[16px]">
          {data.map((item, index) => (
            <div
              key={index}
              className="flex items-center gap-x-[12px] border border-[#CDDDD9] p-[12px] rounded-[8px]"
            >
              <div className="p-[8px] rounded-[6px] bg-cyan-100">
                {item.icon}
              </div>
              <div>
                <div className="text-[14px] font-[500] text-neutral-700">
                  {item.value}
                </div>
                <div className="text-[12px] font-[400] text-neutral-500">
                  {item.label}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AppCard>
  );
};

export default CurrentEmploymentYTD;
